import { ClassNames } from '@emotion/react'
import React, { type ComponentProps, type ReactNode, useMemo } from 'react'

import type { SemanticColorToken } from '../themes'
import {
  CssVarNames,
  CssVars,
  getWrappedCssVars,
  type ThemeColorToken,
} from '../themes'
import {
  _InternalDeclareCssVariables,
  getColorDefinition,
  useResolveToThemeColorToken,
  useTheme,
  useThemeElevation,
} from './ThemeProvider'

/**
 * A helper component to use when you have `children` that you want to be a base shade of a
 * color and change visually on hover. To use this, attach the provided CSS classes to your
 * root DOM node and use the provided newly-created CSS variable in whatever CSS property you
 * want to have colored and reactive to user hovers and presses (e.g. `background-color`).
 */
export function HoverState({
  children,
  color,
  isActive = false,
}: {
  /**
   * The color you wish to use as your hoverable color. For example, the background color for
   * your interactive element, if you want the background color to change on hover.
   */
  color: SemanticColorToken | ThemeColorToken | null | undefined | false
  /**
   * Children to render. This is a render function that will be passed the following props:
   *
   * * colorVariable (string): The CSS variable to use in your CSS property, as `var(...)`.
   * * rootClassName (string): The CSS class name to apply to your root child element.
   * * hoverableClassName (string): The CSS class name to apply to your interactive child
   *     element when you want the color to be able to change on hover/pressed (e.g. when
   *     your interactive element is not disabled).
   */
  children: ComponentProps<typeof HoverStateInner>['children']
  /**
   * Specify `true` if you want the color to take on the active color by default.
   */
  isActive?: ComponentProps<typeof HoverStateInner>['isActive']
}) {
  const variableNameBase = '--rh__hoverable-color'
  const resolvedColor = useResolveToThemeColorToken(color)

  if (!resolvedColor) {
    return (
      <>
        {children({
          colorVariable: getWrappedCssVars(`${variableNameBase}-none`),
          hoverableClassName: '',
          rootClassName: '',
        })}
      </>
    )
  }

  return (
    <HoverStateInner
      color={resolvedColor}
      isActive={isActive}
      variableNameBase={variableNameBase}
    >
      {children}
    </HoverStateInner>
  )
}

/**
 * This is a helper component for HoverState just to simplify the usage of React hooks.
 *
 * Do not export this or use it outside this file.
 */
function HoverStateInner({
  children,
  color,
  isActive,
  variableNameBase,
}: {
  variableNameBase: string
  color: ThemeColorToken
  children: (args: {
    colorVariable: string
    rootClassName: string
    hoverableClassName: string
  }) => ReactNode
  isActive?: boolean
}) {
  const theme = useTheme()
  const elevation = useThemeElevation()
  const colorDefinition = getColorDefinition(color, theme, elevation)

  const {
    components: { a, b, g, r },
  } = colorDefinition

  // Note(Bill): We're using the segments of the passed in color as a unique id for the color css variable
  // This caps the amount of CSS that we build for this component to be the number of colors we support in our system
  // rather than the infinite amount of CSS that would be generate before with random ids. This will prevent memory leaks for SSR
  const variableName = `${variableNameBase}-${r}-${g}-${b}-${a || 'none'}`

  // Set up the CSS definitions we need. We first map the new variable to the base color,
  // which we'll define via the `rootClassName`. Then via HoverStateDefiner we map it to the
  // hover and pressed color variants inside `:hover` and `:active` selectors, defined via
  // the `hoverableClassName`.
  const variables = useMemo(
    () => ({
      [variableName]: colorDefinition.base,
    }),
    [colorDefinition.base, variableName]
  )
  const hoverVariables = useMemo(
    () => ({
      [variableName]: colorDefinition.hover,
    }),
    [colorDefinition.hover, variableName]
  )
  const activeVariables = useMemo(
    () => ({
      [variableName]: colorDefinition.pressed,
    }),
    [colorDefinition.pressed, variableName]
  )

  return (
    <_InternalDeclareCssVariables variables={variables}>
      {({ rootClassName }) => (
        <HoverStateDefiner
          activeVariables={activeVariables}
          hoverVariables={hoverVariables}
          isActive={!!isActive}
        >
          {({ hoverableClassName }) =>
            children({
              colorVariable: getWrappedCssVars(variableName),
              hoverableClassName,
              rootClassName,
            })
          }
        </HoverStateDefiner>
      )}
    </_InternalDeclareCssVariables>
  )
}

/**
 * A helper component to use when you have `children` that are in a low-opacity shade of the
 * primary color and you want them to have a visual change on hover. This works by redefining
 * the various CSS variables for the `children` (e.g. `semantic.primary`), so you don't need
 * to make any changes manually.
 *
 * Note that we have a legacy usage pattern where we nest a `LowOpacityHoverState` inside a
 * `PrimaryColorOverride` to achieve a hover state using the base variant of a color rather
 * than a low-opacity variant. Instances of this should switch to using `HoverState` instead.
 */
export function LowOpacityHoverState({
  children,
  isActive = false,
}: {
  /**
   * Children to render. This is a render function that will be passed the following prop:
   *
   * * hoverableClassName (string): The CSS class name to apply to your interactive child
   *     element when you want the color to be able to change on hover/pressed (e.g. when
   *     your interactive element is not disabled).
   */
  children: (props: { hoverableClassName: string }) => ReactNode
  /**
   * Specify `true` if you want the color to take on the active color by default.
   */
  isActive?: boolean
}) {
  const theme = useTheme()
  const elevation = useThemeElevation()
  const color = getColorDefinition(
    theme.semanticColors.primary,
    theme,
    elevation
  )
  const hoverVariables = useMemo(
    () => ({
      [CssVarNames.semantic.primary.base]: color.hover,
      [CssVarNames.semantic.primary.light]: color.base,
      [CssVarNames.semantic.primary.lightest]: color.base,
      [CssVarNames.overlay.base]: CssVars.neutral.bg1,
      [CssVarNames.overlay.light]: CssVars.neutral.bg1,
      [CssVarNames.overlay.lightest]: CssVars.neutral.bg1,
    }),
    [color]
  )
  const activeVariables = useMemo(
    () => ({
      [CssVarNames.semantic.primary.base]: color.pressed,
      [CssVarNames.semantic.primary.light]: color.hover,
      [CssVarNames.semantic.primary.lightest]: color.hover,
      [CssVarNames.overlay.base]: CssVars.neutral.bg1,
      [CssVarNames.overlay.light]: CssVars.neutral.bg1,
      [CssVarNames.overlay.lightest]: CssVars.neutral.bg1,
    }),
    [color]
  )
  return (
    <HoverStateDefiner
      activeVariables={activeVariables}
      hoverVariables={hoverVariables}
      isActive={isActive}
    >
      {children}
    </HoverStateDefiner>
  )
}

/**
 * A helper component to use when you have `children` that are in the neutral foreground colors
 * and you want them to have a visual change on hover. This works by redefining the relevant
 * CSS variables for the `children` (e.g. `fg1`), so you don't need to make any changes manually.
 */
const fgColorHoverStateVariables = {
  [CssVarNames.neutral.fg1]: CssVars.neutral.hoverFg1,
  [CssVarNames.neutral.fg2]: CssVars.neutral.hoverFg2,
  [CssVarNames.neutral.fg3]: CssVars.neutral.hoverFg3,
}
const fgColorActiveStateVariables = {
  [CssVarNames.neutral.fg1]: CssVars.neutral.pressedFg1,
  [CssVarNames.neutral.fg2]: CssVars.neutral.pressedFg2,
  [CssVarNames.neutral.fg3]: CssVars.neutral.pressedFg3,
}
/**
 * @deprecated Use `HoverState` instead.
 */
// TODO(eric.lovett): Replace all uses of this with HoverState.
export function ForegroundColorHoverState({
  children,
  isActive = false,
}: {
  children: (props: { hoverableClassName: string }) => ReactNode
  isActive?: boolean
}) {
  return (
    <HoverStateDefiner
      activeVariables={fgColorActiveStateVariables}
      hoverVariables={fgColorHoverStateVariables}
      isActive={isActive}
    >
      {children}
    </HoverStateDefiner>
  )
}

/**
 * A helper component to use when you have `children` that are in the neutral foreground colors
 * and you want them to have a visual change on hover, switching to the current primary color.
 * This works by redefining the relevant CSS variables for the `children` (e.g. `fg1`), so you
 * don't need to make any changes manually.
 *
 * Note that we usually want a neutral foreground color to stay neutral when we hover rather
 * than switch to the primary color. The main case when you'd want to use this instead is when
 * the hoverable element has less visible surface area and therefore needs a more dramatic hover
 * state. You should therefore use `ForegroundColorHoverState` more often than this.
 */
const fgColorPrimaryHoverStateVariables = {
  [CssVarNames.neutral.fg1]: CssVars.semantic.primary.base,
  [CssVarNames.textColor]: CssVars.semantic.primary.base,
}
const fgColorPrimaryActiveStateVariables = {
  [CssVarNames.neutral.fg1]: CssVars.semantic.primary.pressed,
  [CssVarNames.textColor]: CssVars.semantic.primary.base,
}
export function ForegroundColorPrimaryHoverState({
  children,
  isActive = false,
}: {
  /**
   * Children to render. This is a render function that will be passed the following prop:
   *
   * * hoverableClassName (string): The CSS class name to apply to your interactive child
   *     element when you want the color to be able to change on hover/pressed (e.g. when
   *     your interactive element is not disabled).
   */
  children: (props: { hoverableClassName: string }) => ReactNode
  /**
   * Specify `true` if you want the color to take on the active color by default.
   */
  isActive?: boolean
}) {
  return (
    <HoverStateDefiner
      activeVariables={fgColorPrimaryActiveStateVariables}
      hoverVariables={fgColorPrimaryHoverStateVariables}
      isActive={isActive}
    >
      {children}
    </HoverStateDefiner>
  )
}

/**
 * A helper component to use when you have `children` that are in the neutral background colors
 * and you want them to have a visual change on hover. This works by redefining the relevant CSS
 * variables for the `children` (e.g. `bg1`), so you don't need to make any changes manually.
 */
const bgColorHoverStateVariables = {
  [CssVarNames.neutral.bg1]: CssVars.neutral.hoverBg1,
  [CssVarNames.neutral.bg2]: CssVars.neutral.hoverBg2,
  [CssVarNames.neutral.bg3]: CssVars.neutral.hoverBg3,
}
const bgColorActiveStateVariables = {
  [CssVarNames.neutral.bg1]: CssVars.neutral.pressedBg1,
  [CssVarNames.neutral.bg2]: CssVars.neutral.pressedBg2,
  [CssVarNames.neutral.bg3]: CssVars.neutral.pressedBg3,
}
/**
 * @deprecated Use `HoverState` instead.
 */
// TODO(eric.lovett): Replace all uses of this with HoverState.
export function BackgroundColorHoverState({
  children,
  isActive = false,
}: {
  children: (props: { hoverableClassName: string }) => ReactNode
  isActive?: boolean
}) {
  return (
    <HoverStateDefiner
      activeVariables={bgColorActiveStateVariables}
      hoverVariables={bgColorHoverStateVariables}
      isActive={isActive}
    >
      {children}
    </HoverStateDefiner>
  )
}

/**
 * A helper component to change the active CSS variables for `children` to reflect the
 * specified hover and active state theme changes.
 */
function HoverStateDefiner({
  activeVariables,
  children,
  hoverVariables,
  isActive = false,
}: {
  children: (props: { hoverableClassName: string }) => ReactNode
  isActive: boolean
  hoverVariables: ComponentProps<
    typeof _InternalDeclareCssVariables
  >['variables']
  activeVariables: ComponentProps<
    typeof _InternalDeclareCssVariables
  >['variables']
}) {
  return (
    <_InternalDeclareCssVariables variables={hoverVariables}>
      {({ __experimentalCssString: hoverCss }) => (
        <_InternalDeclareCssVariables variables={activeVariables}>
          {({
            __experimentalCssString: activeCss,
            rootClassName: activeClass,
          }) => (
            <ClassNames>
              {({ css }) =>
                children({
                  hoverableClassName: isActive
                    ? activeClass
                    : css(`&:hover {${hoverCss}} &:active {${activeCss}}`),
                })
              }
            </ClassNames>
          )}
        </_InternalDeclareCssVariables>
      )}
    </_InternalDeclareCssVariables>
  )
}
